import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Box, BoxProps, InternalStandardProps } from "@mui/material";
import { AppTypography } from "JS/React/Components/AppTypography";
import {
    EventCategories,
    CourseQuizOption,
    CourseQuizQuestion,
    EventActions,
    EventNames,
    CourseDetail,
} from "JS/Models";
import { AppRadioGroup } from "JS/React/Components/AppRadioGroup";
import { QuizFooter } from "./QuizFooter";
import { CourseLogFirebaseEventSettings } from "../Steps/CourseStep";

const StyledRoot = styled(Box)({});

const StyledQuestion = styled(AppTypography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StyledQuesNo = styled(AppTypography)(({ theme }) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
}));

const StyledMarginTop = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

export interface QuizQuestionProps extends InternalStandardProps<BoxProps> {
    question: CourseQuizQuestion;
    answer: CourseQuizOption;
    disableOptions?: boolean;
    readOnly?: boolean;
    course?: CourseDetail;
    handleLogFirebaseEvents?: (
        type: "course" | "step" | "question",
        settings: CourseLogFirebaseEventSettings,
    ) => void;
    setAnswer?: (v: CourseQuizOption) => void;
}

export const QuizQuestion = (props: QuizQuestionProps) => {
    const {
        className,
        question,
        answer,
        readOnly,
        disableOptions,
        course,
        handleLogFirebaseEvents,
        setAnswer,
        ...rest
    } = props;

    const options = question?.options
        ?.slice()
        ?.sort()
        ?.map((o) => ({
            label: o.description,
            value: `${o.position}`,
        }));

    const correctOption = question?.options?.find(
        (d) => d.position === question?.correct_option,
    );

    useEffect(() => {
        if (!readOnly && course) {
            if (course?.contentType === "Course") {
                handleLogFirebaseEvents("question", {
                    eventNames: EventNames.COURSE_STEP_QUESTION_OPENED,
                    action: EventActions.QUESTION_OPENED,
                    category: EventCategories.COURSE_STEP,
                    questionId: +question.id,
                    questionPos: question.position,
                });
            }
            if (course?.contentType === "GiftCourse") {
                handleLogFirebaseEvents("question", {
                    eventNames: EventNames.GIFT_COURSE_STEP_QUESTION_OPENED,
                    action: EventActions.QUESTION_OPENED,
                    category: EventCategories.GIFT_COURSE_STEP,
                    questionId: +question.id,
                    questionPos: question.position,
                });
            }
        }
    }, [readOnly, question]);

    return (
        <StyledRoot {...rest} className={className}>
            {readOnly && (
                <StyledQuesNo
                    textColor="grey-a100"
                    variant="h5"
                >
                    Question {question.position + 1}
                </StyledQuesNo>
            )}
            <StyledQuestion
                as={readOnly ? AppTypography : StyledMarginTop}
                fontWeight={500}
                textColor="grey-500"
                variant="h4"
                sx={!readOnly ? { marginBottom: (theme) => theme.spacing(1) } : undefined}
            >
                {question.question}
            </StyledQuestion>
            <AppRadioGroup
                onChange={(e) => {
                    if (!disableOptions) {
                        const op = question.options.find(
                            (x) => x.position === +e.target.value,
                        );
                        setAnswer(op);
                    }
                }}
                radios={options}
                value={answer?.position}
            />
            {readOnly && answer?.position !== correctOption?.position && (
                <QuizFooter
                    sx={{ marginTop: (theme) => theme.spacing(3) }}
                    title="Correct Answer:"
                    subTitle={correctOption?.description}
                />
            )}
        </StyledRoot>
    );
};
